// src/pages/budgeting/categories/BudgetCategoryList.jsx
// UPDATED VERSION with Tree/Flat View Pattern (like MilestoneTemplateList)
import React, { useState, useEffect } from 'react';
import { Plus, Eye, Edit2, Trash2, Layers, Filter, RefreshCw, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import BudgetCategoryTreeRow from '../../../components/budgeting/BudgetCategoryTreeRow';
import budgetCategoryService from '../../../services/budgetCategoryService';

/**
 * BudgetCategoryList Page
 * Main page for viewing budget categories by project type
 * Displays hierarchical tree structure with expand/collapse
 * 
 * UPDATED: Using tree/flat view pattern like MilestoneTemplateList
 */
const BudgetCategoryList = () => {
  // State management
  const [selectedProjectType, setSelectedProjectType] = useState('FO');
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('tree'); // 'tree' or 'flat'
  
  // Statistics
  const [stats, setStats] = useState({
    totalCategories: 0,
    level0Count: 0,
    level1Count: 0,
    level2Count: 0,
    groupCount: 0,
    leafCount: 0
  });

  // Project types configuration
  const projectTypes = [
    { value: 'FO', label: 'FO', color: 'bg-green-600', description: 'Fiber Optic' },
    { value: 'TOWER_SITAC', label: 'TOWER SITAC', color: 'bg-purple-600', description: 'Site Acquisition' },
    { value: 'TOWER_CME', label: 'TOWER CME', color: 'bg-blue-600', description: 'Civil, Mechanical, Electrical' }
  ];

  // Load categories on mount and when project type changes
  useEffect(() => {
    loadCategories();
  }, [selectedProjectType]);

  // Filter categories when search query changes
  useEffect(() => {
    filterCategories();
  }, [searchQuery, categories]);

  // Load categories from API (Tree structure)
  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await budgetCategoryService.getRootCategoriesTree(selectedProjectType);
      setCategories(data);
      calculateStats(data);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Gagal memuat budget categories');
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const calculateStats = (data) => {
    let level0 = 0, level1 = 0, level2 = 0, groups = 0, leaves = 0;
    
    const countRecursive = (items, level = 0) => {
      items.forEach(item => {
        if (level === 0) level0++;
        else if (level === 1) level1++;
        else if (level === 2) level2++;
        
        if (item.isGroup) groups++;
        else leaves++;
        
        if (item.children && item.children.length > 0) {
          countRecursive(item.children, level + 1);
        }
      });
    };
    
    countRecursive(data);
    
    setStats({
      totalCategories: level0 + level1 + level2,
      level0Count: level0,
      level1Count: level1,
      level2Count: level2,
      groupCount: groups,
      leafCount: leaves
    });
  };

  // Filter categories based on search query
  const filterCategories = () => {
    if (!searchQuery.trim()) {
      setFilteredCategories(categories);
      return;
    }

    const query = searchQuery.toLowerCase();
    
    const filterRecursive = (items) => {
      return items.reduce((acc, item) => {
        const matchesSearch = 
          item.categoryCode.toLowerCase().includes(query) ||
          item.categoryName.toLowerCase().includes(query) ||
          (item.costType && item.costType.toLowerCase().includes(query)) ||
          (item.subtitle && item.subtitle.toLowerCase().includes(query));

        if (matchesSearch) {
          acc.push(item);
        } else if (item.children && item.children.length > 0) {
          const filteredChildren = filterRecursive(item.children);
          if (filteredChildren.length > 0) {
            acc.push({
              ...item,
              children: filteredChildren
            });
          }
        }
        
        return acc;
      }, []);
    };

    const filtered = filterRecursive(categories);
    setFilteredCategories(filtered);
  };

  // Handle refresh
  const handleRefresh = () => {
    setSearchQuery('');
    loadCategories();
    toast.success('Data refreshed');
  };

  // Handle project type change
  const handleProjectTypeChange = (type) => {
    setSelectedProjectType(type);
    setSearchQuery('');
  };

  // Handle view category
  const handleView = (category) => {
    toast.info(`View category: ${category.categoryName}`);
    // TODO: Implement view modal
  };

  // Handle edit category
  const handleEdit = (category) => {
    toast.info(`Edit category: ${category.categoryName}`);
    // TODO: Implement edit form
  };

  // Handle delete category
  const handleDelete = async (category) => {
    if (window.confirm(`Are you sure you want to delete ${category.categoryName}?`)) {
      try {
        await budgetCategoryService.softDeleteCategory(category.id);
        toast.success('Category deleted successfully!');
        loadCategories(); // Refresh list
      } catch (error) {
        console.error('Error deleting category:', error);
        toast.error('Failed to delete category');
      }
    }
  };

  // Flatten tree for flat view
  const flattenTree = (items, level = 0) => {
    let result = [];
    items.forEach((item, index) => {
      result.push({ ...item, level, displayOrder: index + 1 });
      if (item.children && item.children.length > 0) {
        result = result.concat(flattenTree(item.children, level + 1));
      }
    });
    return result;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-md">
              <Layers className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Budget Category List</h1>
              <p className="text-gray-600 mt-1">
                {selectedProjectType === 'FO' && 'TOWER CME: Civil, Mechanical & Electrical categories'}
                {selectedProjectType === 'TOWER_SITAC' && 'TOWER SITAC: Site Acquisition categories'}
                {selectedProjectType === 'TOWER_CME' && 'TOWER CME: Civil, Mechanical & Electrical categories'}
              </p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by code, name, or type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Project Type Tabs */}
        <div className="flex items-center space-x-2 flex-wrap">
          <Filter className="w-5 h-5 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Project Type:</span>
          <div className="flex space-x-2 flex-wrap gap-2">
            {projectTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => handleProjectTypeChange(type.value)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedProjectType === type.value
                    ? `${type.color} text-white shadow-md`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {type.label}
                {selectedProjectType === type.value && (
                  <span className="ml-2 text-xs opacity-75">({type.description})</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Level 0 */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-md p-4 text-white">
          <div className="text-sm opacity-90">Level 0 (Root)</div>
          <div className="text-3xl font-bold mt-1">{stats.level0Count}</div>
          <div className="text-xs opacity-75 mt-1">Root categories</div>
        </div>

        {/* Level 1 */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md p-4 text-white">
          <div className="text-sm opacity-90">Level 1 (Child)</div>
          <div className="text-3xl font-bold mt-1">{stats.level1Count}</div>
          <div className="text-xs opacity-75 mt-1">Child categories</div>
        </div>

        {/* Level 2 */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-md p-4 text-white">
          <div className="text-sm opacity-90">Level 2 (Grandchild)</div>
          <div className="text-3xl font-bold mt-1">{stats.level2Count}</div>
          <div className="text-xs opacity-75 mt-1">Grandchild categories</div>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Table Header with View Mode Toggle */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Budget Categories ({stats.totalCategories})
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {stats.groupCount} groups, {stats.leafCount} leaf categories
              </p>
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">View:</span>
              <button
                onClick={() => setViewMode('tree')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'tree'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Tree
              </button>
              <button
                onClick={() => setViewMode('flat')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'flat'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Flat
              </button>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
              <span className="ml-3 text-gray-600">Loading categories...</span>
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="text-center py-12">
              <Layers className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No budget categories found</p>
              {searchQuery && (
                <p className="text-gray-400 text-sm mt-2">Try adjusting your search query</p>
              )}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-red-600 to-blue-600 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider w-16">
                    No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    Code & Category Name
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-20">
                    Level
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-24">
                    Type
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-32">
                    Can Add BOQ
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-24">
                    Status
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-32">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {viewMode === 'tree' ? (
                  // Tree View
                  filteredCategories.map((category, index) => (
                    <BudgetCategoryTreeRow
                      key={category.id}
                      category={{ ...category, displayOrder: index + 1 }}
                      level={0}
                      onView={handleView}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))
                ) : (
                  // Flat View
                  flattenTree(filteredCategories).map((category) => (
                    <BudgetCategoryTreeRow
                      key={category.id}
                      category={{ ...category, children: [] }} // Remove children for flat view
                      level={category.level}
                      onView={handleView}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Table Footer */}
        {!loading && filteredCategories.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <div>
                Menampilkan {stats.totalCategories} budget categories
              </div>
              <div className="flex items-center space-x-2">
                {searchQuery && (
                  <span className="text-blue-600 font-medium">
                    Filtered results
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetCategoryList;